import React, { useState } from 'react';
import { ThumbsUp, MapPin } from 'lucide-react';
import { upvoteStartup, downvoteStartup } from '../services/api';

const StartupCard = ({ startup, onVoteChange }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [upvoted, setUpvoted] = useState(false);
  const [votes, setVotes] = useState(startup.rating);
  const [isVoting, setIsVoting] = useState(false);

  const handleUpvote = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isVoting) return;
    setIsVoting(true);
    
    try {
      if (upvoted) {
        await downvoteStartup(startup.id);
        setVotes(votes - 1);
      } else {
        await upvoteStartup(startup.id);
        setVotes(votes + 1);
      }
      setUpvoted(!upvoted);
      if (onVoteChange) onVoteChange();
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const locationText = [startup.city, startup.countryName].filter(Boolean).join(', ');

  return (
    <a
      href={`/startup/${startup.slug}`}
      className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] bg-gray-100 overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}
        <img
          src={startup.image}
          alt={`${startup.name} preview`}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x250?text=' + encodeURIComponent(startup.name);
            setImageLoaded(true);
          }}
        />
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-1">
          {startup.name}
        </h3>
        <p className="mt-1 text-sm text-gray-600 line-clamp-2 min-h-[40px]">
          {startup.description}
        </p>

        {/* Location & Team */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <span>{startup.country}</span>
            <span className="truncate max-w-[120px]">{startup.team}</span>
          </div>
          <button
            onClick={handleUpvote}
            disabled={isVoting}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
              upvoted
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } ${isVoting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <ThumbsUp className={`w-3.5 h-3.5 ${upvoted ? 'fill-emerald-600' : ''}`} />
            <span>{votes}</span>
          </button>
        </div>

        {/* Location Info */}
        {locationText && (
          <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{locationText}</span>
          </div>
        )}

        {/* Category Badge */}
        <div className="mt-2">
          <span className="inline-block px-2.5 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
            {startup.category}
          </span>
        </div>
      </div>
    </a>
  );
};

export default StartupCard;
